
import { SEO } from "@/components/SEO";
import { Breadcrumbs } from "@/components/Breadcrumbs";
import { useQuery } from "@tanstack/react-query";
import { ContractorsGrid } from "@/components/contractors-list/ContractorsGrid";
import { supabase } from "@/integrations/supabase/client";
import { Contractor } from "@/types/contractor";
import { Bug, WrenchIcon, ClipboardCheck, ScrollText, Shield, Clock, CheckCircle } from "lucide-react";

const BestPestControlPage = () => {
  const breadcrumbItems = [
    { label: "Best Pest Control Services in OKC" }
  ];

  const { data: contractors = [], isLoading } = useQuery<Contractor[]>({
    queryKey: ['contractors', 'pest-control'],
    queryFn: async () => {
      console.log('Fetching pest control contractors...');
      const { data, error } = await supabase
        .from('contractors')
        .select('*')
        .eq('service_type', 'Pest Control')
        .order('featured', { ascending: false })
        .order('rating', { ascending: false });
      
      if (error) {
        console.error('Error fetching contractors:', error);
        throw error;
      }
      return data || [];
    }
  });

  return (
    <div className="min-h-screen">
      <SEO
        title="Best Pest Control Services in OKC | Home Pros OKC"
        description="Looking for the best pest control services in OKC? Our trusted exterminators handle all pest issues from termites to rodents. Compare top-rated pest control companies today!"
        canonicalUrl="/best-pest-control-okc"
      />
      
      <div className="bg-gradient-to-b from-primary/20 to-background py-16">
        <div className="container mx-auto px-4">
          <Breadcrumbs items={breadcrumbItems} />
          
          <div className="text-center max-w-3xl mx-auto">
            <Bug className="w-16 h-16 mx-auto text-primary mb-6" />
            <h1 className="text-4xl font-bold mb-6">Best Pest Control Services in OKC</h1>
            <p className="text-lg text-muted-foreground">
              When pests invade your home, you need reliable experts fast. Our network of licensed pest control specialists in Oklahoma City provides professional solutions for all your residential and commercial needs.
            </p>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-4 py-12">
        {/* Services Section with Icons */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 mb-12">
          <div className="flex items-start gap-4 p-6 bg-card rounded-lg shadow-sm">
            <WrenchIcon className="w-8 h-8 text-primary flex-shrink-0" />
            <div>
              <h3 className="font-semibold mb-2">Expert Extermination</h3>
              <p className="text-muted-foreground">Professional pest elimination</p>
            </div>
          </div>
          
          <div className="flex items-start gap-4 p-6 bg-card rounded-lg shadow-sm">
            <Clock className="w-8 h-8 text-primary flex-shrink-0" />
            <div>
              <h3 className="font-semibold mb-2">Fast Response</h3>
              <p className="text-muted-foreground">Quick service when you need it</p>
            </div>
          </div>
          
          <div className="flex items-start gap-4 p-6 bg-card rounded-lg shadow-sm">
            <Shield className="w-8 h-8 text-primary flex-shrink-0" />
            <div>
              <h3 className="font-semibold mb-2">Safe Treatments</h3>
              <p className="text-muted-foreground">Family and pet friendly options</p>
            </div>
          </div>

          <div className="flex items-start gap-4 p-6 bg-card rounded-lg shadow-sm">
            <ClipboardCheck className="w-8 h-8 text-primary flex-shrink-0" />
            <div>
              <h3 className="font-semibold mb-2">Guaranteed Results</h3>
              <p className="text-muted-foreground">Effective, lasting solutions</p>
            </div>
          </div>
        </div>

        {/* Core Services Section */}
        <div className="mb-12">
          <h2 className="text-2xl font-semibold mb-6 text-center">Pest Control Services</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-12 gap-y-4 max-w-4xl mx-auto">
            <ul className="space-y-3">
              <li className="flex items-center gap-2">
                <CheckCircle className="w-5 h-5 text-primary" />
                <span>Termite treatment & prevention</span>
              </li>
              <li className="flex items-center gap-2">
                <CheckCircle className="w-5 h-5 text-primary" />
                <span>Rodent control</span>
              </li>
              <li className="flex items-center gap-2">
                <CheckCircle className="w-5 h-5 text-primary" />
                <span>Ant, roach & spider control</span>
              </li>
            </ul>
            <ul className="space-y-3">
              <li className="flex items-center gap-2">
                <CheckCircle className="w-5 h-5 text-primary" />
                <span>Bed bug elimination</span>
              </li>
              <li className="flex items-center gap-2">
                <CheckCircle className="w-5 h-5 text-primary" />
                <span>Mosquito treatment</span>
              </li>
              <li className="flex items-center gap-2">
                <CheckCircle className="w-5 h-5 text-primary" />
                <span>Quarterly prevention plans</span>
              </li>
            </ul>
          </div>
        </div>

        {/* Tips Section */}
        <div className="mb-12 bg-card p-8 rounded-lg">
          <h2 className="text-2xl font-semibold mb-6 text-center">Questions to Ask Your Pest Control Company</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-12 gap-y-4 max-w-4xl mx-auto">
            <ul className="space-y-3">
              <li className="flex items-center gap-2">
                <ScrollText className="w-5 h-5 text-primary" />
                <span>Are you licensed and insured?</span>
              </li>
              <li className="flex items-center gap-2">
                <ScrollText className="w-5 h-5 text-primary" />
                <span>What chemicals do you use?</span>
              </li>
              <li className="flex items-center gap-2">
                <ScrollText className="w-5 h-5 text-primary" />
                <span>Do you offer guarantees?</span>
              </li>
            </ul>
            <ul className="space-y-3">
              <li className="flex items-center gap-2">
                <ScrollText className="w-5 h-5 text-primary" />
                <span>How many treatments are needed?</span>
              </li>
              <li className="flex items-center gap-2">
                <ScrollText className="w-5 h-5 text-primary" />
                <span>Are preventative measures included?</span>
              </li>
              <li className="flex items-center gap-2">
                <ScrollText className="w-5 h-5 text-primary" />
                <span>Do you offer maintenance plans?</span>
              </li>
            </ul>
          </div>
        </div>

        {/* Contractors List Section */}
        <div>
          <h2 className="text-2xl font-semibold mb-6 text-center">Top Pest Control Companies in Oklahoma City</h2>
          {!isLoading && contractors.length > 0 ? (
            <ContractorsGrid contractors={contractors} viewMode="grid" />
          ) : (
            <p className="text-muted-foreground text-center py-8">
              {isLoading ? "Loading contractors..." : "No contractors found."}
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

export default BestPestControlPage;
